import React, { useState } from 'react';
import { useAuth } from './auth/AuthContext';
import { useIsSubscribed } from '../hooks/useIsSubscribed';
import { useTranslation } from '../hooks/useTranslation';
import stripePortalService from '../services/stripePortalService';
import { trackCTAClick, trackFeatureUsed } from '../services/analyticsService';
import './ManageSubscriptionButton.css';

/**
 * Manage Subscription Button Component
 * 
 * A button that opens the Stripe Customer Portal for users to manage their subscription.
 * Only shows if user is authenticated and has a subscription.
 * 
 * @example
 * <ManageSubscriptionButton />
 * 
 * @example
 * <ManageSubscriptionButton 
 *   buttonText="Manage Billing"
 *   className="custom-button"
 * />
 */
const ManageSubscriptionButton = ({ 
  buttonText,
  className = '',
  variant = 'primary', // 'primary' | 'secondary' | 'link'
  showIcon = true,
  onSuccess,
  onError,
}) => {
  const { currentUser } = useAuth();
  const t = useTranslation();
  const { isSubscribed, loading: subscriptionLoading } = useIsSubscribed();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Use provided buttonText or default translation
  const displayButtonText = buttonText || t('subscription.manageButton', 'Manage Subscription');
  // Don't show if user is not authenticated
  if (!currentUser) {
    return null;
  }

  // Don't show if subscription status is still loading
  if (subscriptionLoading) {
    return (
      <button 
        className={`manage-subscription-button ${className}`}
        disabled
      >
        {showIcon && <span className="button-icon">⏳</span>}
        {t('subscription.loading', 'Loading...')}
      </button>
    );
  }

  // Optionally only show if user has a subscription
  // Remove this check if you want to show the button to all authenticated users
  if (!isSubscribed) {
    return null;
  }

  const handleClick = async (e) => {
    e.preventDefault();
    
    // Track portal open click
    trackCTAClick({
      section: 'billing',
      action: 'open_subscription_portal',
      source: 'button'
    });
    
    setLoading(true);
    setError(null);

    try {
      // Open the Stripe portal (automatically redirects)
      await stripePortalService.openPortal({
        returnUrl: window.location.href, // Return to current page after portal
        redirect: true,
      });
      
      // Track successful portal redirect
      trackFeatureUsed({
        section: 'billing',
        feature: 'subscription_portal',
        action: 'redirect_to_stripe',
        source: 'button'
      });
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error opening Stripe portal:', err);
      setError(err.message || t('subscription.portalFailed', 'Failed to open subscription portal'));
      
      // Track portal error
      trackFeatureUsed({
        section: 'billing',
        feature: 'subscription_portal',
        action: 'error',
        source: 'button',
        additionalParams: {
          error: err.message
        }
      });
      
      // Call error callback if provided
      if (onError) {
        onError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const buttonClasses = [
    'manage-subscription-button',
    `manage-subscription-button--${variant}`,
    loading && 'manage-subscription-button--loading',
    error && 'manage-subscription-button--error',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className="manage-subscription-button-wrapper">
      <button
        className={buttonClasses}
        onClick={handleClick}
        disabled={loading}
        type="button"
      >
        {loading ? (
          <>
            {showIcon && <span className="button-icon">⏳</span>}
            {t('subscription.openingPortal', 'Opening Portal...')}
          </>
        ) : (
          <>
            {showIcon && <span className="button-icon">⚙️</span>}
            {displayButtonText}
          </>
        )}
      </button>
      
      {error && (
        <div className="manage-subscription-button-error" role="alert">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error}</span>
          <button 
            className="error-dismiss"
            onClick={() => {
              trackFeatureUsed({
                section: 'billing',
                feature: 'error_dismiss',
                action: 'dismiss',
                source: 'button'
              });
              setError(null);
            }}
            aria-label={t('subscription.dismissError', 'Dismiss error')}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageSubscriptionButton;

